/**
 * Time-sync verification.
 *
 * Why this matters for DOOH
 * --------------------------
 * The player signs every authenticated request with an X-Sensar-Timestamp
 * header that the server validates against its own clock — anything more
 * than ±5 min off is rejected as a replay. If a TV in a venue has NTP
 * disabled (or, more commonly, has the venue's wrong manual time), every
 * request silently 401s. Players get stuck in "perpetually offline" and
 * nobody notices because there's no visible failure.
 *
 * Proof-of-play and presence records are also timestamped client-side
 * (so we can correlate to the precise playback frame); if those drift
 * by minutes the analytics layer aggregates impressions onto the wrong
 * hour bucket.
 *
 * What this module does
 * ---------------------
 * On boot, fetch `/health` (which returns the server's ISO `time`),
 * measure RTT, estimate offset, and store it in-memory + log. If the
 * delta exceeds WARN_THRESHOLD_MS we report a Sentry warning so the
 * fleet operator notices BEFORE the server starts dropping requests.
 *
 * We deliberately do NOT auto-correct the client clock — we have no
 * permission to and applying an offset everywhere is invasive. We just
 * surface the drift loudly.
 */

import { CONFIG } from '@/config';
import { logger } from './logger';
import { captureException } from './sentry';

const WARN_THRESHOLD_MS = 60_000;       // 1 min — log warning
const ERROR_THRESHOLD_MS = 4 * 60_000;  // 4 min — signature rejection at 5

interface TimeSyncResult {
  serverTimeMs: number;
  clientTimeMs: number;
  rttMs: number;
  /** serverTime - clientTime, in ms. Positive means client is BEHIND server. */
  offsetMs: number;
  withinTolerance: boolean;
}

let lastResult: TimeSyncResult | null = null;

/**
 * Run one time-sync probe. Idempotent. Stores the result in module
 * state — `getLastTimeSync()` lets the diagnostic screen show it.
 */
export async function probeTimeSync(): Promise<TimeSyncResult | null> {
  const t0 = Date.now();
  try {
    const res = await fetch(`${CONFIG.API_BASE_URL}/health`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    const t1 = Date.now();
    if (!res.ok) return null;
    const body = (await res.json()) as { time?: string };
    if (!body.time) return null;

    const serverTimeMs = Date.parse(body.time);
    if (!Number.isFinite(serverTimeMs)) return null;

    // RTT-aware offset estimate. Assume symmetric latency: server's clock
    // when we read it is approximately serverTime + rtt/2. So:
    //   client at t1 ≈ server at (serverTime + rtt/2)
    //   offset = (serverTime + rtt/2) - t1
    const rttMs = t1 - t0;
    const offsetMs = serverTimeMs + rttMs / 2 - t1;
    const abs = Math.abs(offsetMs);

    const result: TimeSyncResult = {
      serverTimeMs,
      clientTimeMs: t1,
      rttMs,
      offsetMs,
      withinTolerance: abs < WARN_THRESHOLD_MS,
    };
    lastResult = result;

    if (abs >= ERROR_THRESHOLD_MS) {
      const msg =
        `Clock drift ${Math.round(offsetMs / 1000)}s — signed requests will be rejected by API soon. ` +
        `Check NTP / device clock at the venue.`;
      logger.error(msg, {
        offsetMs,
        rttMs,
        serverTime: new Date(serverTimeMs).toISOString(),
        clientTime: new Date(t1).toISOString(),
      });
      // Also surface to Sentry as a real exception so it groups across
      // the fleet — fingerprint on the rounded magnitude so a 4-min vs
      // 5-min drift on the same TV doesn't generate two issues.
      captureException(new Error('Clock drift exceeds server tolerance'), {
        offsetMs,
        rttMs,
      });
    } else if (abs >= WARN_THRESHOLD_MS) {
      logger.warn('[time-sync] noticeable clock drift', {
        offsetMs,
        rttMs,
      });
    } else {
      logger.info('[time-sync] clock OK', {
        offsetMs,
        rttMs,
      });
    }

    return result;
  } catch (err) {
    logger.debug('[time-sync] probe failed (will retry next sync)', {
      err: String(err),
    });
    return null;
  }
}

export function getLastTimeSync(): TimeSyncResult | null {
  return lastResult;
}
